import { useGetEmployeesPerformance } from '@agos/api-client/hr'
import { useGetOrdersHeatmap } from '@agos/api-client/orders'
import {
  useGetStatisticsCategories,
  useGetStatisticsProducts,
  useGetStatisticsSales,
  useGetStatisticsVat,
} from '@agos/api-client/reporting'
import { T, downloadTextFile, formatCount, tt } from '@agos/shared'
import {
  BUTTON_SECONDARY,
  ButtonGroup,
  cn,
  FilterBar,
  FilterSelect,
  PageHeader,
  Spinner,
  StatCard,
  useToast,
} from '@agos/ui'
import {
  AlertCircle,
  BarChart3,
  ChevronDown,
  ClipboardList,
  Clock,
  Download,
  Receipt,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react'
import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts'

import { Fragment, useMemo, useState, type ReactNode } from 'react'

import {
  HEAT_STEPS,
  PERIODS,
  averagePerHour,
  categoriesMatchProducts,
  categoryColour,
  categoryNames,
  centsShareBasisPoints,
  chartGridlines,
  compare,
  compareDays,
  csvFileName,
  dailyRowsAgree,
  dayBarReading,
  emptyPeriodNote,
  filterByCategory,
  formatCents,
  formatChange,
  formatDayLabel,
  formatRate,
  formatShare,
  groupByCategory,
  heatCells,
  heatOpacity,
  heatTextIsLight,
  labelEvery,
  namesNeedingQuotes,
  paymentColour,
  paymentSlices,
  paymentsAgree,
  performanceRows,
  performanceTotals,
  periodLabel,
  previousRange,
  productCsv,
  productCsvMediaType,
  rangeFor,
  rangeParams,
  reconcileRevenue,
  salesTotals,
  shareBasisPoints,
  sortPerformance,
  sortProducts,
  statisticsParams,
  type CategoryStat,
  type DayBarReading,
  type Performance,
  type PerformanceSort,
  type PeriodKey,
  type ProductStat,
  type SalesStatistics,
  type SortKey,
  vatRows,
  vatTotals,
  weekdayLabel,
} from './sales-report'

/**
 * `/statistics` — the shape of trade over a window.
 *
 * **FB-104.** Two revenue totals sit a few centimetres apart and differ. `/statistics/sales` answers
 * 44 807,65 € for July; the product table sums to 44 736,25 €. The 71,40 € gap is what an order carries
 * beyond its lines, and legacy explains neither figure. Named here, with the difference stated.
 *
 * **FB-105.** The CSV export is comma-delimited with dot decimals, so a Slovak-locale spreadsheet reads
 * every amount as text — and 124 of the 1 113 product names carry a comma of their own, protected by
 * quoting that has no escape for an embedded quote. Semicolons, comma decimals, RFC 4180 escaping.
 *
 * **FB-106.** `ordersPerHour` is rendered straight, and seven of the thirteen employees have no hours
 * recorded while carrying 906 of the month's 2 259 orders. All seven read *0,00*, which sorts them last
 * and says they did nothing per hour when it means nobody clocked them in.
 *
 * Ported from legacy `pages/statistics.tsx` (1 050 lines). Exported as `StatisticsContent` too, because
 * `/analytics` embeds it as its first tab.
 */

export function StatisticsPage() {
  return <StatisticsContent />
}

export function StatisticsContent({ hideHeader = false }: { hideHeader?: boolean } = {}) {
  const { toast } = useToast()
  const [today] = useState(() => new Date())
  const [period, setPeriod] = useState<PeriodKey>('today')
  const [custom, setCustom] = useState(() => {
    const range = rangeFor('today', new Date())
    return range
  })
  const [productSort, setProductSort] = useState<SortKey>('revenue')
  const [category, setCategory] = useState<string | null>(null)
  const [grouped, setGrouped] = useState(true)
  const [performanceSort, setPerformanceSort] = useState<PerformanceSort>('orders')
  /** The bar the pointer or the keyboard is on, read out above the chart — §9.103. */
  const [reading, setReading] = useState<DayBarReading | null>(null)

  const range = rangeFor(period, today, custom)
  const before = previousRange(range)

  // `date_from` / `date_to` here; `from` / `to` on the other two. Sending the wrong pair does not fail
  // — the server substitutes its default window and answers with a plausible number.
  const { data: salesData, isLoading: loadingSales } = useGetStatisticsSales(statisticsParams(range))
  const { data: previousData } = useGetStatisticsSales(statisticsParams(before))
  const { data: categoryData } = useGetStatisticsCategories(statisticsParams(range))
  const { data: productData, isLoading: loadingProducts } = useGetStatisticsProducts(
    statisticsParams(range),
  )
  const { data: heatmapData } = useGetOrdersHeatmap(rangeParams(range))
  const { data: performanceData } = useGetEmployeesPerformance(rangeParams(range))
  // §10A.64 / §9.105 — the fourth destination of §10A.3's per-line VAT snapshot.
  const { data: vatData } = useGetStatisticsVat(statisticsParams(range))
  // `statistics.today_vs_yesterday` — always the two real days, never the picked period, because the
  // panel is named after them. Two whole days: see `compareDays` for why the legacy subtitle is not
  // being carried over with the numbers.
  const { data: todayData } = useGetStatisticsSales(statisticsParams(rangeFor('today', today)))
  const { data: yesterdayData } = useGetStatisticsSales(
    statisticsParams(rangeFor('yesterday', today)),
  )

  const sales = (salesData ?? {}) as SalesStatistics
  const products = useMemo(() => (productData ?? []) as ProductStat[], [productData])
  const categories = useMemo(() => (categoryData ?? []) as CategoryStat[], [categoryData])
  const staff = useMemo(
    () => performanceRows((performanceData ?? []) as Performance[]),
    [performanceData],
  )

  const totals = salesTotals(sales)
  const change = compare(sales, (previousData ?? {}))
  const reconciliation = reconcileRevenue(sales, products)
  const slices = paymentSlices(sales)
  const shown = filterByCategory(products, category)
  const shownTotalCents = shown.reduce(
    (sum, product) => sum + Math.round(Number(product.revenue ?? 0) * 100),
    0,
  )
  const groups = groupByCategory(shown)
  const staffTotals = performanceTotals(staff)
  const { cells, busiest } = heatCells((heatmapData ?? {}))
  /** The busiest hour's count — every cell's shade is relative to it. */
  const peakOrders = busiest?.orders ?? 0
  // §9.196 — which category groups are open. Collapsed is the default, so the set holds the
  // exceptions: empty is the screen as it first draws.
  const [openGroups, setOpenGroups] = useState<ReadonlySet<string>>(new Set())
  const versusYesterday = compareDays((todayData ?? {}), (yesterdayData ?? {}))
  const vat = useMemo(() => vatRows(vatData), [vatData])
  const vatSummed = vatTotals(vat)
  const days = sales.revenueByDay ?? []
  /** §9.197 — the peak the day chart's grid is drawn against, in cents. */
  const dayPeakCents = Math.max(
    0,
    ...days.map((day) => Math.round(Number(day.revenue ?? 0) * 100)),
  )
  const peakDay = days.reduce(
    (best, day) => (Number(day.revenue ?? 0) > Number(best?.revenue ?? -1) ? day : best),
    days[0],
  )

  const exportCsv = () => {
    if (products.length === 0) {
      toast({ title: tt('Nie je čo exportovať') })
      return
    }
    const file = downloadTextFile(
      csvFileName(range),
      productCsv(products, productSort),
      productCsvMediaType(),
    )
    toast({
      title: tt('Export hotový'),
      description: `${formatCount(products.length)} ${tt('produktov · bodkočiarka a desatinná čiarka, aby to tabuľkový editor sčítal')}`,
      // Deliberately says which shape it is: legacy's comma-delimited, dot-decimal export cannot be
      // summed in a Slovak-locale spreadsheet, which is the one thing an export of amounts must do.
    })
    void file
  }

  return (
    <div className="space-y-5">
      {!hideHeader && (
        <PageHeader
          title={tt('Štatistiky')}
          description={tt('Tržby, produkty, platby a výkon za zvolené obdobie.')}
          icon={<BarChart3 className="w-5 h-5" />}
          actions={
            <button
              type="button"
              onClick={exportCsv}
              className={cn(BUTTON_SECONDARY, 'flex items-center gap-2 px-4 py-2 rounded-xl text-sm')}
            >
              <Download className="w-4 h-4" />
              <T>Export CSV</T>
            </button>
          }
        />
      )}

      <div className="flex flex-wrap items-center gap-2" data-period-picker>
        <ButtonGroup
          value={period}
          onChange={(next) => setPeriod(next as PeriodKey)}
          options={PERIODS.map((key) => ({ value: key, label: periodLabel(key) }))}
        />
        <input
          type="date"
          value={custom.from}
          onChange={(event) => {
            setCustom({ ...custom, from: event.target.value })
            setPeriod('custom')
          }}
          aria-label={tt('Od')}
          className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
        />
        <input
          type="date"
          value={custom.to}
          onChange={(event) => {
            setCustom({ ...custom, to: event.target.value })
            setPeriod('custom')
          }}
          aria-label={tt('Do')}
          className="bg-background border border-border rounded-lg px-3 py-2 text-sm"
        />
        <span className="text-xs text-muted-foreground" data-range-label>
          {range.from} → {range.to}
        </span>
        {/* §9.211 - *"a list of orders that carried a discount, filterable by date"*. The archive
          * already does the listing and now names each discount's source (§9.210), so the way in is
          * a link carrying this period rather than a second screen that would answer differently. */}
        <a
          href={`/lists?discounted=true&from=${range.from}&to=${range.to}`}
          data-discounted-link
          className="ml-auto text-xs font-semibold text-primary underline underline-offset-2"
        >
          <T>Objednávky so zľavou v tomto období</T>
        </a>
      </div>

      {loadingSales ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-sales-totals>
            <StatCard
              label={tt('Tržba')}
              value={formatCents(totals.revenueCents)}
              sub={`${tt('predchádzajúce obdobie')} ${formatChange(change.changeBasisPoints)}`}
              icon={change.differenceCents >= 0 ? TrendingUp : TrendingDown}
              color={change.differenceCents >= 0 ? 'emerald' : 'red'}
            />
            <StatCard label={tt('Objednávok')} value={formatCount(totals.orders)} icon={Receipt} color="blue" />
            <StatCard
              label={tt('Priemerná objednávka')}
              value={formatCents(totals.averageCents)}
              icon={Receipt}
              color="violet"
            />
            <StatCard
              label={tt('Najsilnejší deň')}
              value={peakDay === undefined ? '-' : formatDayLabel(peakDay.date)}
              sub={peakDay === undefined ? undefined : formatCents(Math.round(Number(peakDay.revenue ?? 0) * 100))}
              icon={BarChart3}
              color="amber"
            />
          </div>

          {/* FB-104 - the two totals, and what stands between them. */}
          {!reconciliation.agrees && (
            <p className="flex items-start gap-1.5 text-xs text-muted-foreground" data-revenue-gap>
              <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
              <span>
                <T>Tržba z objednávok je</T>{' '}
                <strong>{formatCents(reconciliation.orderRevenueCents)}</strong>,{' '}
                <T>súčet produktov</T>{' '}
                <strong>{formatCents(reconciliation.lineRevenueCents)}</strong> -{' '}
                <T>rozdiel</T> <strong>{formatCents(reconciliation.differenceCents)}</strong>{' '}
                <T>
                  nesie objednávka mimo svojich položiek (rozvoz a podobne). Obe čísla sú správne, len
                  merajú iné.
                </T>
              </span>
            </p>
          )}

          {!dailyRowsAgree(sales) && (
            <p className="flex items-start gap-1.5 text-xs text-amber-500" data-days-disagree>
              <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
              <T>Denné riadky nedávajú uvedený súčet - server vrátil nekonzistentné čísla.</T>
            </p>
          )}
          {!paymentsAgree(sales) && (
            <p className="flex items-start gap-1.5 text-xs text-amber-500" data-payments-disagree>
              <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
              <T>Rozpad platieb nedáva uvedený súčet.</T>
            </p>
          )}

          {/* `statistics.today_vs_yesterday` - enabled for this franchise and drawn by nothing until
            * §9.103. Always the two real days, whatever period is picked above. */}
          <Card
            title={tt('Dnes vs Včera')}
            note={tt('celé dni - dnešok sa ešte neskončil')}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-today-vs-yesterday>
              <StatCard
                label={tt('Tržba dnes')}
                value={formatCents(versusYesterday.todayCents)}
                sub={`${formatCount(versusYesterday.todayOrders)} ${tt('objednávok')}`}
                icon={Receipt}
                color="blue"
              />
              <StatCard
                label={tt('Tržba včera')}
                value={formatCents(versusYesterday.yesterdayCents)}
                sub={`${formatCount(versusYesterday.yesterdayOrders)} ${tt('objednávok')}`}
                icon={Receipt}
                color="violet"
              />
              <StatCard
                label={tt('Rozdiel')}
                value={formatCents(versusYesterday.differenceCents)}
                icon={versusYesterday.differenceCents >= 0 ? TrendingUp : TrendingDown}
                color={versusYesterday.differenceCents >= 0 ? 'emerald' : 'red'}
              />
              <StatCard
                label={tt('Zmena')}
                // Yesterday took nothing, so there is no share to state — not 0 %, which would read
                // as "the same as yesterday".
                value={
                  versusYesterday.changeBasisPoints === null
                    ? '-'
                    : formatChange(versusYesterday.changeBasisPoints)
                }
                sub={versusYesterday.changeBasisPoints === null ? tt('včera bez tržby') : undefined}
                icon={BarChart3}
                color="amber"
              />
            </div>
          </Card>

          {/* The daily bars, as bars rather than as a chart library's idea of one. */}
          <Card title={tt('Tržba po dňoch')}>
            {days.length === 0 ? (
              <EmptyPeriod />
            ) : (
              <>
                {/* The reading, above the chart rather than beside the cursor - §9.103. A native
                  * `title` waits a second, draws where the pointer is and takes no styling, so the
                  * owner could not read the day or its takings off a bar. */}
                <p className="text-xs h-5" data-day-reading aria-live="polite">
                  {reading === null ? (
                    <span className="text-muted-foreground">
                      <T>Ukážte na stĺpec pre tržbu daného dňa.</T>
                    </span>
                  ) : (
                    <span className="tabular-nums">
                      <b>{reading.label}</b> · {formatCents(reading.revenueCents)} ·{' '}
                      {formatCount(reading.orders)} <T>objednávok</T>
                    </span>
                  )}
                </p>
                {/* §9.197 - a scale. The bars were `div`s with no axis and no grid, so July's
                    twenty-eight of them could be compared with each other and read as nothing. The
                    bars are kept - they carry `data-day-bar`, they focus, and the reading line above
                    is better than a native tooltip - and the grid is drawn behind them. */}
                <div className="flex gap-2">
                  <div
                    className="flex flex-col justify-between h-40 text-[9px] text-muted-foreground tabular-nums text-right shrink-0"
                    data-day-axis={dayPeakCents}
                  >
                    {chartGridlines(dayPeakCents).map((value) => (
                      <span key={value}>{formatCents(value)}</span>
                    ))}
                    <span>{formatCents(0)}</span>
                  </div>
                <div className="relative flex-1">
                  <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
                    {chartGridlines(dayPeakCents).map((value) => (
                      <span key={value} className="border-t border-dashed border-border/60" />
                    ))}
                    <span className="border-t border-border" />
                  </div>
                <div className="flex items-end gap-1 h-40 relative" data-day-bars>
                  {days.map((day, index) => {
                    const peak = Math.max(...days.map((other) => Number(other.revenue ?? 0)), 1)
                    const height = Math.round((Number(day.revenue ?? 0) / peak) * 100)
                    const bar = dayBarReading(day)
                    return (
                      <button
                        type="button"
                        key={day.date}
                        className="flex-1 min-w-[6px] h-full flex flex-col justify-end items-center gap-1 rounded focus:outline-none focus:ring-2 focus:ring-primary/40"
                        title={`${bar.date} · ${formatCents(bar.revenueCents)} · ${bar.orders} obj.`}
                        data-day-bar={day.date}
                        onMouseEnter={() => setReading(bar)}
                        onMouseLeave={() => setReading(null)}
                        onFocus={() => setReading(bar)}
                        onBlur={() => setReading(null)}
                      >
                        <span
                          className={cn(
                            'w-full rounded-t transition-colors',
                            reading?.date === bar.date ? 'bg-primary' : 'bg-primary/70',
                          )}
                          style={{ height: `${Math.max(height, 2)}%` }}
                        />
                        {/* §9.197 - thinned, not dropped. Printed only under fifteen days, the
                            twenty-eight-day view had none at all and nothing said why. */}
                        {index % labelEvery(days.length) === 0 && (
                          <span className="text-[9px] text-muted-foreground whitespace-nowrap">
                            {formatDayLabel(day.date)}
                          </span>
                        )}
                      </button>
                    )
                  })}
                </div>
                </div>
                </div>
              </>
            )}
          </Card>

          <Card title={tt('Podľa platby')}>
            {slices.length === 0 ? (
              <EmptyPeriod />
            ) : (
              <div className="space-y-1.5" data-payment-slices>
                {/* §9.195 - legacy's donut. Six identical `bg-primary` bars meant the only thing
                    telling cash from *Karta u kuriéra* was reading the label; on a panel whose job
                    is *where did the money come from*, colour is the answer. The bars stay under it
                    because a donut alone is a shape with no numbers - the same complaint §9.194 was
                    about, and not one to repeat on the panel beside it. */}
                <div className="h-[220px]" data-payment-donut>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={slices}
                        cx="50%"
                        cy="50%"
                        innerRadius={65}
                        outerRadius={95}
                        paddingAngle={4}
                        dataKey="amountCents"
                        nameKey="label"
                      >
                        {slices.map((slice) => (
                          <Cell key={slice.method} fill={paymentColour(slice.method)} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                {slices.map((slice) => (
                  <div key={slice.method} className="flex items-center gap-3" data-payment-slice={slice.method}>
                    <span
                      className="w-3 h-3 rounded-full shrink-0"
                      data-payment-swatch={slice.method}
                      style={{ backgroundColor: paymentColour(slice.method) }}
                    />
                    <span className="text-xs w-40 shrink-0 truncate">{slice.label}</span>
                    <span className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                      <span
                        className="block h-full"
                        style={{
                          width: `${slice.shareBasisPoints / 100}%`,
                          backgroundColor: paymentColour(slice.method),
                        }}
                      />
                    </span>
                    <span
                      className="text-xs font-bold tabular-nums w-24 text-right"
                      style={{ color: paymentColour(slice.method) }}
                    >
                      {formatCents(slice.amountCents)}
                    </span>
                    <span className="text-[11px] text-muted-foreground tabular-nums w-16 text-right">
                      {formatShare(slice.shareBasisPoints)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* §9.105 - the VAT split, grouped by the rate a line was *charged at*. Two rows can
            * share a letter when a rate changed mid-period; that is the report being right. */}
          <Card
            title={tt('Rozpis DPH')}
            note={
              vat.length === 0
                ? undefined
                : `${tt('spolu')} ${formatCents(vatSummed.revenueCents)} · ${tt('z toho DPH')} ${formatCents(vatSummed.vatCents)}`
            }
          >
            {vat.length === 0 ? (
              <EmptyPeriod />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-vat-rows>
                  <thead className="text-muted-foreground">
                    <tr className="text-left">
                      <th className="py-1 font-medium"><T>Sadzba</T></th>
                      <th className="py-1 font-medium"><T>Kategória</T></th>
                      <th className="py-1 font-medium text-right"><T>Položiek</T></th>
                      <th className="py-1 font-medium text-right"><T>Tržba</T></th>
                      <th className="py-1 font-medium text-right"><T>Základ</T></th>
                      <th className="py-1 font-medium text-right"><T>DPH</T></th>
                    </tr>
                  </thead>
                  <tbody>
                    {vat.map((row) => (
                      <tr key={row.key} className="border-t border-border" data-vat-row={row.label}>
                        <td className="py-1.5 font-semibold whitespace-nowrap">{row.label}</td>
                        <td className="py-1.5 text-muted-foreground truncate max-w-[16rem]">
                          {row.categoryName}
                        </td>
                        <td className="py-1.5 text-right tabular-nums">{formatCount(row.lines)}</td>
                        <td className="py-1.5 text-right tabular-nums">
                          {formatCents(row.revenueCents)}
                        </td>
                        <td className="py-1.5 text-right tabular-nums text-muted-foreground">
                          {formatCents(row.netCents)}
                        </td>
                        <td className="py-1.5 text-right tabular-nums">
                          {formatCents(row.vatCents)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {/* Named as legacy names it and as the owner asked for it. It was *Kedy sa objednáva*,
            * which is friendlier and is not what anybody was looking for — the report said "there is
            * no Heatmapa objednávok" about a card that was on the page under another name whenever
            * the period had data. §9.103. */}
          <Card
            title={tt('Heatmapa objednávok')}
            note={
              busiest === null || busiest.orders === 0
                ? undefined
                : `${tt('najviac')} ${weekdayLabel(busiest.weekday)} ${busiest.hour}:00 - ${busiest.orders} ${tt('objednávok')}`
            }
          >
            {busiest === null || busiest.orders === 0 ? (
              <EmptyPeriod />
            ) : (
              /* §9.194 — legacy's grid. The cells were 16px and **empty**, so a shade stood for a
                 number nobody could read and hovering was the only way to get one - which on a phone
                 is no way at all. The count is printed in the cell now, every hour is labelled, and
                 the legend under it says what the shades mean. */
              <div className="overflow-x-auto" data-heatmap>
                <p className="md:hidden text-[10px] text-muted-foreground italic mb-2">
                  <T>← potiahnite pre zobrazenie všetkých hodín →</T>
                </p>
                <div className="inline-grid gap-[3px]" style={{ gridTemplateColumns: 'auto repeat(24, 1fr)' }}>
                  <span />
                  {Array.from({ length: 24 }, (_, hour) => (
                    <span
                      key={hour}
                      className="text-[10px] font-bold text-muted-foreground text-center pb-1.5 min-w-[32px]"
                    >
                      {hour}
                    </span>
                  ))}
                  {Array.from({ length: 7 }, (_, index) => {
                    // The payload is Sunday-first; a Slovak week starts on Monday.
                    const weekday = (index + 1) % 7
                    return (
                      <Fragment key={weekday}>
                        <span className="text-xs font-bold text-muted-foreground pr-2.5 flex items-center justify-end">
                          {weekdayLabel(weekday)}
                        </span>
                        {cells
                          .filter((cell) => cell.weekday === weekday)
                          .map((cell) => (
                            <span
                              key={`${cell.weekday}-${cell.hour}`}
                              title={`${weekdayLabel(cell.weekday)} ${cell.hour}:00 - ${cell.orders}`}
                              data-heat-cell={cell.orders}
                              className="rounded-lg flex items-center justify-center min-w-[32px] h-[32px] text-[10px] font-bold transition-colors"
                              style={{
                                backgroundColor: `rgba(16, 185, 129, ${heatOpacity(cell.orders, peakOrders)})`,
                                // Past halfway the fill is dark enough to swallow dark text — which
                                // is the detail that makes printing the count worth doing.
                                color: heatTextIsLight(cell.orders, peakOrders)
                                  ? 'rgba(255,255,255,0.95)'
                                  : 'rgba(0,0,0,0.55)',
                              }}
                            >
                              {cell.orders > 0 ? cell.orders : ''}
                            </span>
                          ))}
                      </Fragment>
                    )
                  })}
                </div>

                <div className="flex items-center gap-3 mt-5 pt-4 border-t border-border/50">
                  <span className="text-xs font-semibold text-muted-foreground">
                    <T>Menej</T>
                  </span>
                  <div className="flex gap-1" data-heat-legend={HEAT_STEPS.length}>
                    {HEAT_STEPS.map((opacity) => (
                      <span
                        key={opacity}
                        className="w-6 h-6 rounded-md"
                        style={{ backgroundColor: `rgba(16, 185, 129, ${opacity})` }}
                      />
                    ))}
                  </div>
                  <span className="text-xs font-semibold text-muted-foreground">
                    <T>Viac</T>
                  </span>
                </div>
              </div>
            )}
          </Card>
        </>
      )}

      {/* ─── the products ─── */}
      <Card
        title={tt('Top produkty')}
        note={`${formatCount(shown.length)} ${tt('položiek')} · ${formatCents(shownTotalCents)}`}
      >
        <FilterBar
          filters={
            <>
              {/* Segmented, as legacy has them: the selected one is lit, so the screen says which
                * view you are in. The button here was labelled with the *action* — *Zobraziť ako
                * zoznam* while grouped — so nothing on the page stated the current mode, and reading
                * it as a state label got you the opposite of the truth. §9.103. */}
              <ButtonGroup
                value={grouped ? 'grouped' : 'flat'}
                onChange={(next) => setGrouped(next === 'grouped')}
                options={[
                  { value: 'grouped', label: tt('Podľa kategórie') },
                  { value: 'flat', label: tt('Zoznam') },
                ]}
              />
              <ButtonGroup
                value={productSort}
                onChange={(next) => setProductSort(next as SortKey)}
                options={[
                  { value: 'revenue', label: tt('Tržba') },
                  { value: 'quantity', label: tt('Počet') },
                ]}
              />
              {/* This one stays a select. Legacy segments it too, but the franchise has eleven
                * categories and a row of eleven buttons is not the same control at that width. */}
              <FilterSelect
                value={category ?? 'all'}
                label={tt('Kategória')}
                onChange={(next) => setCategory(next === 'all' ? null : next)}
                options={[
                  { value: 'all', label: tt('Všetky kategórie') },
                  ...categoryNames(products).map((name) => ({ value: name, label: name })),
                ]}
              />
            </>
          }
        />

        {!categoriesMatchProducts(categories, products) && (
          <p className="flex items-start gap-1.5 text-xs text-amber-500" data-categories-disagree>
            <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
            <T>Súčet kategórií nesúhlasí so súčtom produktov - obe merajú tržbu z položiek.</T>
          </p>
        )}

          {/* §9.196 - legacy's grouped table. Every group used to be drawn open, so the screen a
              manager opens to ask *which category earns* was a wall to scroll past before two
              headers could be compared - and the header never said the one thing the panel is named
              after: what share of the period the category is. Collapsed by default, for the reason
              §9.176 collapses the permissions. */}
        {loadingProducts ? (
          <div className="flex items-center justify-center py-12">
            <Spinner />
          </div>
        ) : grouped ? (
          <div className="divide-y divide-border/50" data-product-groups>
            {groups.map((group, index) => {
              const colour = categoryColour(index)
              const isOpen = openGroups.has(group.categoryName)
              return (
                <div key={group.categoryName} data-product-group={group.categoryName}>
                  <button
                    type="button"
                    data-group-toggle={group.categoryName}
                    aria-expanded={isOpen}
                    onClick={() =>
                      setOpenGroups((current) => {
                        const next = new Set(current)
                        if (next.has(group.categoryName)) next.delete(group.categoryName)
                        else next.add(group.categoryName)
                        return next
                      })
                    }
                    className="w-full py-3 flex items-center gap-3 hover:bg-muted/20 transition-colors text-left"
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-full shrink-0"
                      data-group-swatch={group.categoryName}
                      style={{ backgroundColor: colour }}
                    />
                    <span className="font-bold flex-1 truncate">{group.categoryName}</span>
                    <span className="text-xs text-muted-foreground tabular-nums">
                      {formatCount(group.quantity)} ks
                    </span>
                    <span className="font-bold text-sm tabular-nums" style={{ color: colour }}>
                      {formatCents(group.revenueCents)}
                    </span>
                    <span
                      className="text-xs text-muted-foreground w-16 text-right tabular-nums"
                      data-group-share={group.categoryName}
                    >
                      {formatShare(centsShareBasisPoints(group.revenueCents, shownTotalCents))}
                    </span>
                    <ChevronDown
                      className={cn(
                        'w-4 h-4 shrink-0 text-muted-foreground transition-transform',
                        isOpen && 'rotate-180',
                      )}
                    />
                  </button>
                  {isOpen && (
                    <ProductRows products={group.products} totalCents={shownTotalCents} />
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          <div data-product-list>
            <ProductRows products={sortProducts(shown, productSort)} totalCents={shownTotalCents} />
          </div>
        )}

        {namesNeedingQuotes(products) > 0 && (
          <p className="text-[11px] text-muted-foreground" data-csv-note>
            {/* Said where the export button is, because it is why the delimiter is a semicolon. */}
            <strong>{namesNeedingQuotes(products)}</strong>{' '}
            <T>
              názvov obsahuje čiarku, preto export používa bodkočiarku a desatinnú čiarku - inak by
              tabuľkový editor sumy neprečítal ako čísla.
            </T>
          </p>
        )}
      </Card>

      {/* ─── the people ─── */}
      <Card
        title={tt('Výkon zamestnancov')}
        note={`${formatCount(staffTotals.orders)} ${tt('objednávok')} · ${formatCents(staffTotals.revenueCents)}`}
      >
        {/* FB-106 - seven of thirteen, and 906 of 2 259 orders, with no hours behind them. */}
        {staffTotals.withoutHours > 0 && (
          <p className="flex items-start gap-1.5 text-xs text-amber-500" data-no-hours>
            <AlertCircle className="w-4 h-4 shrink-0 mt-px" />
            <span>
              <strong>{staffTotals.withoutHours}</strong>{' '}
              <T>zamestnancov nemá v tomto období zapísané hodiny, hoci odviezli</T>{' '}
              <strong>{formatCount(staffTotals.ordersWithoutHours)}</strong>{' '}
              <T>
                objednávok. Ich objednávky na hodinu sa nedajú vypočítať - nie sú nulové, sú nemerané.
              </T>
            </span>
          </p>
        )}

        {/* §9.197 - legacy's four tiles. AGOS's table already carries more than legacy's (hours,
            per-hour, delivery time, km, and FB-106's warning); what it lacked was the summary above
            it. Orders and revenue were in the card's note - a sentence rather than a figure - and
            the average per hour was on no screen at all. */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-performance-totals>
          <StatCard label={tt('Zamestnanci')} value={formatCount(staffTotals.staff)} icon={Users} color="blue" />
          <StatCard
            label={tt('Celkom objednávok')}
            value={formatCount(staffTotals.orders)}
            icon={ClipboardList}
            color="violet"
          />
          <StatCard
            label={tt('Celkom tržba')}
            value={formatCents(staffTotals.revenueCents)}
            icon={BarChart3}
            color="emerald"
          />
          <StatCard
            label={tt('Priem. obj./hod')}
            // Over the staff whose hours are known — dividing by everybody would report a rate
            // diluted by exactly the people FB-106's warning above exists to name.
            value={formatRate(averagePerHour(staff))}
            icon={Clock}
            color="amber"
            sub={staffTotals.withoutHours > 0 ? tt('len z meraných') : undefined}
          />
        </div>

        <ButtonGroup
          value={performanceSort}
          onChange={(next) => setPerformanceSort(next as PerformanceSort)}
          options={[
            { value: 'orders', label: tt('Objednávky') },
            { value: 'revenue', label: tt('Tržba') },
            { value: 'per-hour', label: tt('Za hodinu') },
          ]}
        />

        <div className="overflow-x-auto" data-performance>
          <table className="w-full text-xs">
            <thead className="text-muted-foreground uppercase tracking-wider">
              <tr>
                <th className="text-left py-2">{tt('Zamestnanec')}</th>
                <th className="text-right py-2">{tt('Objednávky')}</th>
                <th className="text-right py-2">{tt('Tržba')}</th>
                <th className="text-right py-2">{tt('Hodiny')}</th>
                <th className="text-right py-2">{tt('Za hodinu')}</th>
                <th className="text-right py-2">{tt('Doručenie')}</th>
                <th className="text-right py-2">km</th>
              </tr>
            </thead>
            <tbody>
              {sortPerformance(staff, performanceSort).map((row) => (
                <tr key={row.id} className="border-t border-border" data-performance-row={row.id}>
                  <td className="py-2">
                    <p className="font-semibold">{(row.name ?? '').trim()}</p>
                    <p className="text-[11px] text-muted-foreground">{row.position}</p>
                  </td>
                  <td className="py-2 text-right tabular-nums">{formatCount(row.ordersCount ?? 0)}</td>
                  <td className="py-2 text-right tabular-nums">{formatCents(row.revenueCents)}</td>
                  <td className="py-2 text-right tabular-nums">
                    {(row.hoursWorked ?? 0) > 0 ? formatRate(row.hoursWorked ?? 0) : '-'}
                  </td>
                  <td
                    className={cn(
                      'py-2 text-right tabular-nums',
                      row.perHour === null && 'text-amber-500',
                    )}
                    data-per-hour={row.perHour === null ? 'unmeasured' : 'measured'}
                  >
                    {formatRate(row.perHour)}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {row.avgDeliveryTime === null || row.avgDeliveryTime === undefined
                      ? '-'
                      : `${formatRate(row.avgDeliveryTime)} min`}
                  </td>
                  <td className="py-2 text-right tabular-nums">
                    {row.totalKm === null || row.totalKm === undefined ? '-' : formatRate(row.totalKm)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}

// ─── small pieces ────────────────────────────────────────────────────────────

function ProductRows({ products, totalCents }: { products: ProductStat[]; totalCents: number }) {
  return (
    <div className="space-y-1">
      {products.map((product) => (
        <div
          key={product.productId}
          className="flex items-center gap-3 text-xs"
          data-product-row={product.productId}
        >
          <span className="min-w-0 flex-1 truncate">{product.productName}</span>
          <span className="tabular-nums text-muted-foreground w-16 text-right">
            {formatCount(product.quantitySold ?? 0)} ks
          </span>
          <span className="tabular-nums w-24 text-right font-semibold">
            {formatCents(Math.round(Number(product.revenue ?? 0) * 100))}
          </span>
          <span className="tabular-nums text-muted-foreground w-14 text-right">
            {formatShare(shareBasisPoints(product, totalCents))}
          </span>
        </div>
      ))}
    </div>
  )
}

/**
 * What a card says when its period holds nothing — §9.103.
 *
 * The three period cards used to disappear instead, and the page opens on `today`, which is empty
 * every morning. The owner reported the heatmap as both *not working* and *never built*; an absent
 * card cannot tell those apart, and legacy renders `Žiadne dáta` in the same box rather than
 * removing it.
 */
function EmptyPeriod() {
  return (
    <p className="text-xs text-muted-foreground py-6 text-center" data-empty-period>
      {emptyPeriodNote()}
    </p>
  )
}

function Card({
  title,
  note,
  children,
}: {
  title: string
  note?: string
  children: ReactNode
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-bold">{title}</h2>
        {note !== undefined && <span className="text-xs text-muted-foreground">{note}</span>}
      </div>
      {children}
    </div>
  )
}
